import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Ban, 
  Unlock, 
  Snowflake, 
  Plus, 
  Minus,
  AlertTriangle,
  Shield,
  Clock,
  Wallet,
  Gift,
  Trophy,
  Gamepad2,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { usersAPI } from '../lib/api';
import { formatCurrency, formatDateTime, getStatusColor, getInitials } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const colorClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };
  
  return (
    <span className={`badge ${colorClasses[color]}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletDialog, setWalletDialog] = useState(false);
  const [noteDialog, setNoteDialog] = useState(false);
  const [walletData, setWalletData] = useState({
    wallet_type: 'real',
    adjustment_type: 'credit',
    amount: '',
    reason: '',
  });
  const [noteText, setNoteText] = useState('');

  const fetchUser = async () => {
    try {
      setLoading(true);
      const [userRes, txRes] = await Promise.all([
        usersAPI.getById(userId),
        usersAPI.getTransactions(userId, { limit: 50 }),
      ]);
      setUser(userRes.data);
      setTransactions(txRes.data);
    } catch (error) {
      toast.error('Failed to fetch user data');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const handleBlock = async () => {
    try {
      await usersAPI.block(userId);
      toast.success('User blocked');
      fetchUser();
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleUnblock = async () => {
    try {
      await usersAPI.unblock(userId);
      toast.success('User unblocked');
      fetchUser();
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleFreezeWallet = async () => {
    try {
      await usersAPI.freezeWallet(userId);
      toast.success('Wallet frozen');
      fetchUser();
    } catch (error) {
      toast.error('Failed to freeze wallet');
    }
  };

  const handleWalletAdjust = async () => {
    try {
      await usersAPI.adjustWallet({
        user_id: userId,
        ...walletData,
        amount: parseFloat(walletData.amount),
      });
      toast.success('Wallet adjusted successfully');
      setWalletDialog(false);
      setWalletData({ wallet_type: 'real', adjustment_type: 'credit', amount: '', reason: '' });
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust wallet');
    }
  };

  const handleAddNote = async () => {
    try {
      await usersAPI.update(userId, { notes: noteText });
      toast.success('Note added');
      setNoteDialog(false);
      setNoteText('');
      fetchUser();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div data-testid="user-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/users')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Profile Header */}
      <div className="profile-header">
        <div className={`profile-avatar ${user.suspicious_flag ? 'bg-gradient-to-br from-red-500 to-red-700' : ''}`}>
          {getInitials(user.name)}
        </div>
        <div className="profile-info flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="profile-name">{user.name}</h1>
            <StatusBadge status={user.status} />
            <StatusBadge status={user.kyc_status} />
            {user.suspicious_flag && (
              <span className="badge badge-danger flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Suspicious
              </span>
            )}
          </div>
          <p className="profile-id">{user.id}</p>
          <p className="text-sm text-muted-foreground mt-1">{user.email} • {user.mobile}</p>
          <p className="text-sm text-muted-foreground">Country: {user.country} • VIP Level: {user.vip_level}</p>
        </div>
        <div className="quick-actions">
          {user.status === 'active' ? (
            <Button variant="outline" size="sm" onClick={handleBlock} className="text-red-400" data-testid="block-user-btn">
              <Ban className="w-4 h-4 mr-2" />
              Block
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleUnblock} data-testid="unblock-user-btn">
              <Unlock className="w-4 h-4 mr-2" />
              Unblock
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleFreezeWallet} data-testid="freeze-wallet-btn">
            <Snowflake className="w-4 h-4 mr-2" />
            Freeze Wallet
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWalletDialog(true)} data-testid="adjust-wallet-btn">
            <CreditCard className="w-4 h-4 mr-2" />
            Adjust Wallet
          </Button>
          <Button variant="outline" size="sm" onClick={() => setNoteDialog(true)} data-testid="add-note-btn">
            Add Note
          </Button>
        </div>
      </div>

      {/* Wallet Cards */}
      <div className="wallet-grid mb-6">
        <div className="wallet-card">
          <p className="wallet-label">Real Money</p>
          <p className="wallet-value text-emerald-400">{formatCurrency(user.wallet_balance)}</p>
        </div>
        <div className="wallet-card">
          <p className="wallet-label">Bonus</p>
          <p className="wallet-value text-amber-400">{formatCurrency(user.bonus_balance)}</p>
        </div>
        <div className="wallet-card">
          <p className="wallet-label">Winnings</p>
          <p className="wallet-value text-blue-400">{formatCurrency(user.winnings_balance)}</p>
        </div>
        <div className="wallet-card">
          <p className="wallet-label">Locked Bonus</p>
          <p className="wallet-value text-muted-foreground">{formatCurrency(user.locked_bonus)}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <Wallet className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-lg font-bold font-mono">{formatCurrency(user.total_deposits)}</p>
          <p className="text-xs text-muted-foreground">Total Deposits</p>
        </div>
        <div className="stat-card">
          <CreditCard className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-lg font-bold font-mono">{formatCurrency(user.total_withdrawals)}</p>
          <p className="text-xs text-muted-foreground">Total Withdrawals</p>
        </div>
        <div className="stat-card">
          <Gamepad2 className="w-5 h-5 text-violet-400 mb-2" />
          <p className="text-lg font-bold font-mono">{user.total_games_played}</p>
          <p className="text-xs text-muted-foreground">Games Played</p>
        </div>
        <div className="stat-card">
          <Trophy className="w-5 h-5 text-primary mb-2" />
          <p className="text-lg font-bold font-mono">{formatCurrency(user.total_rake_paid)}</p>
          <p className="text-xs text-muted-foreground">Rake Paid</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="transactions" className="data-table-container">
        <TabsList className="tab-list border-b-0">
          <TabsTrigger value="transactions" className="tab-item">Transactions</TabsTrigger>
          <TabsTrigger value="notes" className="tab-item">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow key={tx.id} className="table-row">
                    <TableCell className="text-sm">{formatDateTime(tx.created_at)}</TableCell>
                    <TableCell>
                      <span className={`capitalize ${tx.type === 'deposit' || tx.type === 'winnings' ? 'text-emerald-400' : tx.type === 'withdrawal' ? 'text-red-400' : 'text-amber-400'}`}>
                        {tx.type}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(tx.amount, tx.currency)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={tx.status} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {tx.id.slice(0, 12)}...
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="notes" className="mt-0 p-4">
          {user.notes && user.notes.length > 0 ? (
            <div className="space-y-3">
              {user.notes.map((note, idx) => (
                <div key={idx} className="p-3 bg-muted rounded-lg text-sm">
                  {note}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No notes added</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Wallet Adjustment Dialog */}
      <Dialog open={walletDialog} onOpenChange={setWalletDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Wallet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Wallet Type</Label>
              <Select
                value={walletData.wallet_type}
                onValueChange={(v) => setWalletData({ ...walletData, wallet_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="real">Real Money</SelectItem>
                  <SelectItem value="bonus">Bonus</SelectItem>
                  <SelectItem value="winnings">Winnings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Adjustment Type</Label>
              <Select
                value={walletData.adjustment_type}
                onValueChange={(v) => setWalletData({ ...walletData, adjustment_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Credit (+)</SelectItem>
                  <SelectItem value="debit">Debit (-)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={walletData.amount}
                onChange={(e) => setWalletData({ ...walletData, amount: e.target.value })}
                data-testid="wallet-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                placeholder="Enter reason for adjustment"
                value={walletData.reason}
                onChange={(e) => setWalletData({ ...walletData, reason: e.target.value })}
                data-testid="wallet-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalletDialog(false)}>Cancel</Button>
            <Button onClick={handleWalletAdjust} data-testid="confirm-wallet-adjust-btn">
              Confirm Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Internal Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter note..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              data-testid="note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote} data-testid="confirm-add-note-btn">
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
